import { api } from "./base_service";
import type { ApiError, Result } from "./types";
import { formatDate } from "@/app/(app)/_lib/format";
import { apiEndpoints } from "@/src/config/api/endpoints";

export type NotificationAction = "dismiss" | "download";

export type AppNotification = {
  id: string;
  heading: string;
  subheading: string;
  date: string;
  read: boolean;
  action: NotificationAction;
  downloadUrl?: string;
};

interface NotificationRaw {
  uuid: string;
  title: string;
  message?: string;
  url?: string;
  severity?: number;
  relevant_from?: string;
  relevant_until?: string | null;
  monitoring?: boolean;
  acknowledged?: boolean;
}

interface NotificationPage {
  results: NotificationRaw[];
  next: string | null;
}

function extractPage(data: unknown): NotificationPage {
  if (Array.isArray(data)) return { results: data as NotificationRaw[], next: null };
  const obj = (data ?? {}) as { results?: unknown; notifications?: unknown; next?: unknown };
  if (Array.isArray(obj.results)) {
    return {
      results: obj.results as NotificationRaw[],
      next: typeof obj.next === "string" ? obj.next : null,
    };
  }
  if (Array.isArray(obj.notifications))
    return { results: obj.notifications as NotificationRaw[], next: null };
  return { results: [], next: null };
}

function formatNotificationDate(value: string | number | undefined): string {
  if (!value) return "";
  let ms: number;
  if (typeof value === "number") {
    ms = value < 1e12 ? value * 1000 : value;
  } else {
    ms = new Date(value).getTime();
  }
  if (Number.isNaN(ms)) return "";
  return formatDate(new Date(ms));
}

function adaptNotification(raw: NotificationRaw): AppNotification {
  return {
    id: String(raw.uuid),
    heading: raw.title ?? "",
    subheading: raw.message ?? "",
    date: formatNotificationDate(raw.relevant_from),
    read: raw.acknowledged ?? false,
    action: raw.url ? "download" : "dismiss",
    downloadUrl: raw.url,
  };
}

export async function listNotifications(): Promise<Result<AppNotification[]>> {
  try {
    const data = await api.get<unknown>(apiEndpoints.notifications().list());
    return { ok: true, value: extractPage(data).results.map(adaptNotification) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export async function markNotificationRead(id: string): Promise<Result<void>> {
  try {
    await api.post(apiEndpoints.notifications().acknowledge(id), { acknowledged: true });
    return { ok: true, value: undefined };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export function downloadNotificationFile(notification: AppNotification): Result<void> {
  if (!notification.downloadUrl) {
    return {
      ok: false,
      error: {
        status: 0,
        code: "not_found",
        message: "No file is attached to this notification.",
        requestId: "client",
      },
    };
  }
  if (typeof window !== "undefined") {
    window.open(notification.downloadUrl, "_blank", "noopener,noreferrer");
  }
  return { ok: true, value: undefined };
}
